import { Paper, Title, Text, Stack } from '@mantine/core';
import { BarChart } from '@mantine/charts';
import type { Asset } from '@/types/models';

interface AssetValueChartProps {
  assets: Asset[];
}

export function AssetValueChart({ assets }: AssetValueChartProps) {
  // Calculate total value and value by status
  const totalValue = assets.reduce((sum, asset) => sum + (asset.cost || 0), 0);

  const valueByStatus = assets.reduce(
    (acc, asset) => {
      const status = asset.status || 'Unknown';
      acc[status] = (acc[status] || 0) + (asset.cost || 0);
      return acc;
    },
    {} as Record<string, number>
  );

  // Prepare data for chart
  const chartData = Object.entries(valueByStatus).map(([status, value]) => ({
    status,
    value: Number(value.toFixed(2)),
  }));

  if (assets.length === 0) {
    return (
      <Paper p="md" withBorder>
        <Title order={3} mb="md">
          Asset Value
        </Title>
        <Text c="dimmed" ta="center" py="xl">
          No asset data available
        </Text>
      </Paper>
    );
  }

  return (
    <Paper p="md" withBorder>
      <Stack gap="md">
        <div>
          <Title order={3}>Asset Value</Title>
          <Text size="sm" c="dimmed">
            Total value: ${totalValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Text>
        </div>

        {chartData.length > 0 ? (
          <BarChart
            h={300}
            data={chartData}
            dataKey="status"
            series={[{ name: 'value', label: 'Value ($)', color: 'teal.6' }]}
            tickLine="y"
            gridAxis="y"
            valueFormatter={(value) =>
              `$${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            }
          />
        ) : (
          <Text c="dimmed" ta="center" py="xl">
            No value data to display
          </Text>
        )}
      </Stack>
    </Paper>
  );
}
